
// Fix: Added explicit casting of 'lang' to 'Language' in calculateVertexAndFortune call to resolve union type mismatch.

import React, { useState, useEffect } from 'react';
import { Sparkles, Loader2, Wallet, ArrowRight, ShieldCheck, Star, Zap, Gem, Info, Heart, Target, TrendingUp, Calendar, RefreshCw, Flame, ShieldAlert } from 'lucide-react';
import { calculateVertexAndFortune, updateMemberProfile } from '../services/geminiService';
import { BirthData, VertexFortuneResult, MemberRecord, Language } from '../types';
import PayPalButtons from './PayPalButtons';
import { translations } from '../translations';

interface VertexFortuneCalculatorProps {
  member?: MemberRecord | null;
  onSuccess?: (result: VertexFortuneResult) => void;
  lang?: Language;
}

const VertexFortuneCalculator: React.FC<VertexFortuneCalculatorProps> = ({ member, onSuccess, lang = 'en' }) => {
  const [result, setResult] = useState<VertexFortuneResult | null>(member?.vertex_fortune || null);
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [showPayment, setShowPayment] = useState(false);
  const [birthData, setBirthData] = useState<BirthData>({
    name: member?.name || '',
    date: member?.birth_date || '',
    time: member?.birth_time || '',
    location: member?.birth_location || ''
  });

  const t = translations[lang] || translations.en;

  useEffect(() => {
    let interval: any;
    if (loading) {
      interval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 98) return prev;
          return prev + (prev < 30 ? 1 : prev < 70 ? 0.5 : 0.2);
        });
      }, 500);
    } else {
      setLoadingProgress(0);
    }
    return () => clearInterval(interval);
  }, [loading]);

  const handlePaymentSuccess = async () => {
    setLoading(true);
    setShowPayment(false);
    try {
      // Fix: Cast 'lang' as 'Language' to ensure it strictly matches the expected union type.
      const res = await calculateVertexAndFortune(birthData, lang as Language);
      setResult(res);
      if (member) {
        await updateMemberProfile(member.id, { vertex_fortune: res });
      }
      if (onSuccess) onSuccess(res);
    } catch (err) {
      alert("Celestial connection interrupted. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <div className="animate-fade-in space-y-12 pb-12" id="exclusive-points">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/10 text-amber-500 text-[10px] uppercase font-bold tracking-[0.3em] rounded-full mb-4">
             Destiny Protocol Unlocked
          </div>
          <h3 className="text-4xl md:text-5xl font-serif text-white mb-4 uppercase tracking-[0.2em]">The Arc of Destiny</h3>
          <div className="h-px w-48 bg-gradient-to-r from-transparent via-amber-500 to-transparent mx-auto opacity-50" />
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Vertex Result */}
          <div className="bg-slate-900/40 backdrop-blur-xl border border-amber-500/20 p-8 md:p-12 rounded-sm relative overflow-hidden group shadow-2xl animate-fade-in">
            <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
              <Zap size={160} className="text-amber-500" />
            </div>
            
            <div className="flex items-center gap-6 mb-8">
              <div className="p-4 bg-amber-500 text-slate-950 rounded-full shadow-[0_0_25px_rgba(245,158,11,0.5)]">
                <Zap size={32} />
              </div>
              <div>
                <h4 className="text-amber-400 text-xs uppercase tracking-[0.4em] font-bold mb-1">The Vertex</h4>
                <p className="text-3xl font-serif text-white">{result.vertex.sign} • {result.vertex.degree.toFixed(2)}°</p>
                <p className="text-[10px] uppercase text-indigo-500 font-bold tracking-widest mt-1">Gateway of Fate • House {result.vertex.house}</p>
              </div>
            </div>

            <div className="space-y-8 relative z-10">
              <div className="p-6 bg-slate-950/60 border-l-4 border-amber-500 rounded-sm text-left">
                <p className="text-lg text-indigo-50 font-light italic leading-relaxed">
                  "{result.vertex.meaning}"
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-4 text-left">
                  <div className="flex items-center gap-2 text-amber-500/80 font-bold uppercase text-[9px] tracking-widest">
                    <Target size={12}/> Fated Lessons
                  </div>
                  <p className="text-xs text-indigo-200/70 leading-relaxed">{result.vertex.fated_lessons}</p>
                </div>
                <div className="space-y-4 text-left">
                  <div className="flex items-center gap-2 text-amber-500/80 font-bold uppercase text-[9px] tracking-widest">
                    <Heart size={12}/> Relationship Impact
                  </div>
                  <p className="text-xs text-indigo-200/70 leading-relaxed">{result.vertex.relationship_impact}</p>
                </div>
              </div>

              <div className="pt-6 border-t border-indigo-900/30 text-left">
                <div className="flex items-center gap-2 text-rose-400 font-bold uppercase text-[9px] tracking-widest mb-3">
                  <Zap size={12}/> Karmic Trigger Point
                </div>
                <p className="text-sm text-indigo-100/60 font-light italic">"{result.vertex.karmic_trigger}"</p>
              </div>
            </div>
          </div>

          {/* Part of Fortune Result */}
          <div className="bg-slate-900/40 backdrop-blur-xl border border-indigo-500/20 p-8 md:p-12 rounded-sm relative overflow-hidden group shadow-2xl animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
              <Gem size={160} className="text-indigo-500" />
            </div>
            
            <div className="flex items-center gap-6 mb-8">
              <div className="p-4 bg-indigo-600 text-white rounded-full shadow-[0_0_25px_rgba(79,70,229,0.5)]">
                <Gem size={32} />
              </div>
              <div>
                <h4 className="text-indigo-400 text-xs uppercase tracking-[0.4em] font-bold mb-1">Part of Fortune</h4>
                <p className="text-3xl font-serif text-white">{result.part_of_fortune.sign} • {result.part_of_fortune.degree.toFixed(2)}°</p>
                <p className="text-[10px] uppercase text-indigo-500 font-bold tracking-widest mt-1">Point of Abundance • House {result.part_of_fortune.house}</p>
              </div>
            </div>

            <div className="space-y-8 relative z-10 text-left">
              <div className="p-6 bg-slate-950/60 border-l-4 border-indigo-500 rounded-sm">
                <p className="text-lg text-indigo-50 font-light italic leading-relaxed">
                  "{result.part_of_fortune.meaning}"
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-indigo-400/80 font-bold uppercase text-[9px] tracking-widest">
                    <TrendingUp size={12}/> Prosperity Path
                  </div>
                  <p className="text-xs text-indigo-200/70 leading-relaxed">{result.part_of_fortune.abundance_path}</p>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-indigo-400/80 font-bold uppercase text-[9px] tracking-widest">
                    <Sparkles size={12}/> Hidden Talents
                  </div>
                  <p className="text-xs text-indigo-200/70 leading-relaxed">{result.part_of_fortune.hidden_talents}</p>
                </div>
              </div>

              <div className="pt-6 border-t border-indigo-900/30">
                <div className="flex items-center gap-2 text-emerald-400 font-bold uppercase text-[9px] tracking-widest mb-3">
                  <Calendar size={12}/> Peak Prosperity Cycles
                </div>
                <p className="text-sm text-indigo-100/60 font-light italic">"{result.part_of_fortune.best_day_for_fortune}"</p>
              </div>
            </div>
          </div>
        </div>

        {/* Critical Degrees Section */}
        {result.critical_degrees && result.critical_degrees.length > 0 && (
          <div className="space-y-8 animate-fade-in">
             <div className="text-center">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-rose-500/10 text-rose-500 text-[9px] uppercase font-bold tracking-[0.2em] rounded-full mb-4">
                   <ShieldAlert size={12}/> Potent Placements Detected
                </div>
                <h3 className="text-3xl font-serif text-white uppercase tracking-widest">Critical Degrees</h3>
                <p className="text-indigo-400/60 text-sm max-w-lg mx-auto italic mt-2">
                  "Specific degrees hold magnified karmic intensity. Your map contains extraordinary power points."
                </p>
             </div>

             <div className="grid gap-6">
                {result.critical_degrees.map((cd, i) => (
                  <div key={i} className="bg-slate-900/60 border border-rose-500/20 p-8 rounded-sm hover:border-rose-500/50 transition-all group flex flex-col md:flex-row gap-8 items-start text-left shadow-2xl relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none group-hover:scale-110 transition-transform">
                       <Flame size={120} className="text-rose-500" />
                    </div>
                    <div className="flex-shrink-0">
                       <div className="w-20 h-20 bg-rose-500 text-slate-950 rounded-full flex items-center justify-center text-3xl font-bold shadow-[0_0_30px_rgba(244,63,94,0.3)]">
                          {cd.degree}°
                       </div>
                    </div>
                    <div className="flex-1 space-y-4">
                       <div className="flex items-center gap-4">
                          <h4 className="text-xl font-serif text-white uppercase tracking-widest">{cd.planet} in {cd.sign}</h4>
                          <span className="px-3 py-1 bg-rose-500/10 border border-rose-500/30 text-[9px] uppercase font-bold text-rose-400 tracking-widest rounded-full">Intensity: {cd.power}</span>
                       </div>
                       <p className="text-indigo-100 leading-relaxed font-light italic text-lg">
                          "{cd.meaning}"
                       </p>
                    </div>
                  </div>
                ))}
             </div>
          </div>
        )}

        <div className="p-10 md:p-16 bg-gradient-to-br from-indigo-950/40 to-slate-900/40 border border-indigo-900/50 rounded-sm text-center relative overflow-hidden group">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(245,158,11,0.05)_0%,_transparent_70%)] animate-pulse" />
          <div className="relative z-10">
            <h5 className="text-amber-500 text-xs uppercase tracking-[0.5em] font-bold mb-8">Synergy Masterclass</h5>
            <p className="text-xl md:text-2xl text-indigo-50 leading-relaxed font-light italic max-w-4xl mx-auto">
              "{result.synergy_summary}"
            </p>
          </div>
        </div>

        <div className="flex justify-center pt-8">
           <button onClick={() => setResult(null)} className="px-10 py-4 border-2 border-indigo-900 text-indigo-400 hover:text-white hover:border-amber-500/50 uppercase tracking-widest text-[10px] font-bold rounded-sm transition-all flex items-center gap-3">
             <RefreshCw size={14}/> Map Another Mystery
           </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto animate-fade-in">
      {!showPayment ? (
        <div className="bg-slate-900/80 border border-indigo-900/50 p-8 md:p-12 rounded-sm shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 p-8 opacity-5">
             <Sparkles size={120} className="text-amber-500" />
          </div>
          
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/10 text-amber-500 text-[9px] uppercase font-bold tracking-[0.2em] rounded-full mb-4">
              <Gem size={12}/> Premium Mastery Tool
            </div>
            <h3 className="text-3xl font-serif text-white mb-2">Unlock Your Fate & Fortune</h3>
            <p className="text-indigo-300/70 text-sm italic">"Discover the fated points of encounter, the secret path to your abundance, and your hidden power degrees."</p>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); setShowPayment(true); }} className="space-y-6 text-left">
            <div className="grid grid-cols-2 gap-6">
               <div className="col-span-2">
                  <label className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold mb-2 block ml-1">Name of the Seeker</label>
                  <input required type="text" value={birthData.name} onChange={e => setBirthData({...birthData, name: e.target.value})} className="w-full bg-slate-950 border border-indigo-900 p-4 text-white text-sm rounded-sm focus:border-amber-500 outline-none transition-all" placeholder="Your Name" />
               </div>
               <div>
                  <label className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold mb-2 block ml-1">Birth Date</label>
                  <input required type="text" value={birthData.date} onChange={e => setBirthData({...birthData, date: e.target.value})} className="w-full bg-slate-950 border border-indigo-900 p-4 text-white text-sm rounded-sm focus:border-amber-500 outline-none transition-all" placeholder="YYYY-MM-DD" />
               </div>
               <div>
                  <label className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold mb-2 block ml-1">Birth Time (24h)</label>
                  <input required type="text" value={birthData.time} onChange={e => setBirthData({...birthData, time: e.target.value})} className="w-full bg-slate-950 border border-indigo-900 p-4 text-white text-sm rounded-sm focus:border-amber-500 outline-none transition-all" placeholder="HH:MM" />
               </div>
               <div className="col-span-2">
                  <label className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold mb-2 block ml-1">Birth Location</label>
                  <input required type="text" value={birthData.location} onChange={e => setBirthData({...birthData, location: e.target.value})} className="w-full bg-slate-950 border border-indigo-900 p-4 text-white text-sm rounded-sm focus:border-amber-500 outline-none transition-all" placeholder="City, Country" />
               </div>
            </div>

            <button type="submit" className="w-full py-6 bg-amber-500 text-slate-950 font-bold uppercase tracking-[0.3em] text-[11px] rounded-sm hover:bg-amber-400 transition-all flex items-center justify-center gap-3 shadow-[0_0_40px_rgba(245,158,11,0.2)] mt-4 active:scale-95">
               Initiate Deep Mapping ($20) <ArrowRight size={14}/>
            </button>
          </form>
        </div>
      ) : (
        <div className="bg-slate-900 border border-amber-500/30 p-10 rounded-sm text-center shadow-2xl animate-fade-in">
           <div className="mb-10">
              <ShieldCheck size={48} className="text-emerald-500 mx-auto mb-4" />
              <h4 className="text-2xl font-serif text-white uppercase tracking-widest">Registry Gateway</h4>
              <p className="text-indigo-300 text-xs mt-2 italic">"$20 Secure Exchange for High-Precision Destiny Mapping"</p>
           </div>
           <PayPalButtons amount={20} description="Exclusive Tool: Vertex & Fortune Calculation" onSuccess={handlePaymentSuccess} onError={() => alert("Treasury error.")} />
           <button onClick={() => setShowPayment(false)} className="mt-8 text-indigo-500 text-[9px] uppercase tracking-widest font-bold hover:text-white transition-colors">Abort Ritual</button>
        </div>
      )}

      {loading && (
        <div className="fixed inset-0 z-[10000] bg-slate-950/95 backdrop-blur-2xl flex flex-col items-center justify-center p-8">
           <div className="relative mb-12">
             <div className="absolute inset-0 bg-amber-500/20 blur-3xl rounded-full animate-pulse" />
             <div className="w-32 h-32 md:w-48 md:h-48 rounded-full border-2 border-amber-500/20 flex items-center justify-center animate-[spin_10s_linear_infinite] relative">
               <Loader2 size={64} className="text-amber-500 animate-spin" />
             </div>
           </div>
           
           <div className="max-w-md w-full text-center space-y-6">
              <h4 className="text-2xl md:text-3xl font-serif text-white uppercase tracking-widest animate-pulse">Calculating Sacred Points</h4>
              <p className="text-indigo-400 text-xs uppercase tracking-[0.3em] font-bold">Deep Reasoning Engaged</p>
              
              <div className="w-full h-1.5 bg-indigo-950 rounded-full overflow-hidden border border-indigo-900/30 mt-8">
                 <div 
                   className="h-full bg-amber-500 shadow-[0_0_10px_#f59e0b] transition-all duration-1000 ease-out"
                   style={{ width: `${loadingProgress}%` }}
                 />
              </div>
              
              <p className="text-[10px] text-indigo-200/40 uppercase tracking-widest italic pt-4">
                 Our high-resolution engines are performing 32k reasoning cycles to ensure arc-second accuracy for your fated coordinates and critical degrees.
              </p>
           </div>
        </div>
      )}
    </div>
  );
};

export default VertexFortuneCalculator;
